import { AssessmentSubmissionResponse } from "shared/types/moduleTypes"
import { AssessmentViewOptionsType } from "shared/types/assessmentTypes"
import AssessmentDrawingResponseItem from "./components/AssessmentDrawingResponseItem"
import AssessmentResponseItem from "./components/AssessmentResponseItem"
import useBoolean from "helpers/useBoolean"
import { StudentSubmissionStatusType } from "shared/types/studentTypes"
import React from "react"
import { Button } from "react-bootstrap"
import { AssessmentQuestionContext } from "shared/routes/assignments"
import CustomHTML from "components/CustomHTML"
import { getNotes } from "shared/routes/student"
import { useModel } from "@stem-sims/nexus"
import StudentNotes from "components/Assignment/components/StudentNotes"

interface Props {
    contexts?: AssessmentQuestionContext[]
    assessmentSubmission: AssessmentSubmissionResponse
    submissionStatus: StudentSubmissionStatusType
    assessmentViewOption: AssessmentViewOptionsType
    topicSectionHeader?: string
    oneQuestionMode?: boolean
    viewType: "normal" | "review"
    goBack?: () => void
}

const StudentAssessmentResponses = ({ assessmentSubmission, contexts, submissionStatus, assessmentViewOption, topicSectionHeader, oneQuestionMode, viewType, goBack } : Props) => {
    const [expand, toggleExpand] = useBoolean(true)

    const [currentQuestion, setCurrentQuestion] = React.useState(0)

    const { response: notes, loading: loadingNotes } = useModel({
        model: getNotes,
        props: { studentAssessmentId: assessmentSubmission.studentAssessmentId }
    })

    if (assessmentSubmission === null || assessmentViewOption === "Grade" || loadingNotes) return <></>

    return <div className="fs-5">
        <div className="d-flex justify-content-center">
            <h3 role={viewType === "normal" ? "button" : undefined} onClick={viewType === "normal" ? toggleExpand : undefined} className={`flex-grow-1 text-center fw-bold ${expand && `mb-4`}`}>{topicSectionHeader ?? "Assessment Responses"}</h3>
            {viewType === "normal" && <i role="button" className={`fas ${expand ? `fa-chevron-up` : `fa-chevron-down`} ms-auto`} onClick={toggleExpand} />}
        </div>
        {expand && <ol className="mt-3">
            {oneQuestionMode && assessmentSubmission.responses !== null && (
                <div className="d-flex justify-content-end align-items-center container mb-4 bg-white w-100" style={{ position: "sticky", top: 0, zIndex: 2 }}>
                    {viewType === "review" && <Button className="me-3" variant="theme" onClick={goBack}>Return to Core Ideas</Button>}
                    <Button variant="primary" onClick={() => ( setCurrentQuestion((p) => Math.max(0, p - 1)))} disabled={currentQuestion === 0} className="me-3">Previous Question</Button>
                    <span className="me-3 fw-bold text-muted">{currentQuestion + 1} / {assessmentSubmission.responses.length}</span>
                    <Button variant="primary" onClick={() => ( setCurrentQuestion((p) => Math.min(p + 1, assessmentSubmission.responses.length - 1)))} disabled={currentQuestion === assessmentSubmission.responses.length - 1} >Next Question</Button>
                </div>
            )}
            {assessmentSubmission.responses?.map((response, index) => {
                const context = contexts?.find(c => c.contextId === response.contextId)
                const prevNote = notes?.find(note => note.questionNumber === response.questionNumber)
                return (
                    <span className={oneQuestionMode && currentQuestion !== index ? "d-none" : "position-relative"}>
                        <StudentNotes prevNotes={prevNote?.notes} mode="read" data={null} disableSaving={true} />
                        {context && 
                            <div className="border border-1 rounded p-2 mb-3 text-start">
                                <CustomHTML html={context?.text} />
                            </div>}
                        {response.isDrawing === "Yes" ?
                            <AssessmentDrawingResponseItem 
                                index={index} 
                                response={response} 
                                submissionStatus={submissionStatus} 
                                assessmentViewOption={assessmentViewOption} />
                            : <AssessmentResponseItem 
                                index={index} 
                                response={response} 
                                submissionStatus={submissionStatus} 
                                assessmentViewOption={assessmentViewOption} />}
                    </span>
                )
            })}
        </ol>}
    </div>
}

export default StudentAssessmentResponses
